/**
 * Wallet and resource-bundle helpers for the Altar Forge. Bundles are plain data; `gold` in a
 * bundle maps to `profile.currencies.gold`. The mutating helpers (`credit`, `debit`) work on a
 * Profile the caller already cloned.
 */
import { BLESSED_CAP, BLESSED_OVERFLOW_VENOM, EPIC_PITY_START, FIRST_OFFERING, FREE_TEMPERS_START, gradeIndex } from './forge.constants';
import type { ForgeState, Profile, ResourceBundle, Satchel, ScaleGrade, Wallet } from './types';
import { SCALE_GRADES } from './types';

export const zeroScales = (): Record<ScaleGrade, number> => ({ ashen: 0, jade: 0, gilded: 0, eternal: 0 });

export function emptyWallet(): Wallet {
  return { fangsteel: 0, scales: zeroScales(), venom: 0, moltskin: 0, eyeFragments: 0, sigils: 0, blessed: 0, vaultTokens: [] };
}

/** A new profile's wallet: the First Offering starter cache. */
export function starterWallet(): Wallet {
  const w = emptyWallet();
  w.fangsteel = FIRST_OFFERING.fangsteel;
  w.scales.ashen = FIRST_OFFERING.scales.ashen;
  return w;
}

export function emptySatchel(): Satchel {
  return { fangsteel: 0, scales: zeroScales(), venom: 0, moltskin: 0, sinceEpic: EPIC_PITY_START, belowRare: 0 };
}

export function newForgeState(): ForgeState {
  return {
    rollIndex: 0,
    craftCount: 0,
    sinceEpic: EPIC_PITY_START,
    belowRare: 0,
    eyeMisses: 0,
    daily: { lastClaimDay: null },
    weekly: { isoWeek: null, runs: 0, claimed: false },
    brackets: {},
    codex: [],
    pendingRefine: null,
    freeTempers: FREE_TEMPERS_START, // +forge-sinks
    history: [],
    migrationReport: null,
  };
}

const nn = (x: number | undefined): number => (Number.isFinite(x) ? Math.max(0, Math.floor(x!)) : 0);

/** Sum bundles (missing fields stay missing when every input lacks them). */
export function addBundles(...bs: (ResourceBundle | null | undefined)[]): ResourceBundle {
  const out: ResourceBundle = {};
  const num = ['fangsteel', 'venom', 'moltskin', 'eyeFragments', 'sigils', 'blessed', 'gold'] as const;
  for (const b of bs) {
    if (!b) continue;
    for (const k of num) if (b[k]) out[k] = (out[k] ?? 0) + b[k]!;
    for (const g of SCALE_GRADES) {
      const n = b.scales?.[g];
      if (n) out.scales = { ...out.scales, [g]: (out.scales?.[g] ?? 0) + n };
    }
  }
  return out;
}

/** Non-negative integer copy of a bundle (untrusted input, e.g. world finds). */
export function sanitizeBundle(b: ResourceBundle | null | undefined, allow: { sigils?: boolean } = {}): ResourceBundle {
  if (!b) return {};
  const out: ResourceBundle = {};
  const num = ['fangsteel', 'venom', 'moltskin', 'eyeFragments', 'blessed', 'gold'] as const;
  for (const k of num) if (nn(b[k])) out[k] = nn(b[k]);
  if (allow.sigils && nn(b.sigils)) out.sigils = nn(b.sigils);
  for (const g of SCALE_GRADES) if (nn(b.scales?.[g])) out.scales = { ...out.scales, [g]: nn(b.scales?.[g]) };
  return out;
}

export function isEmptyBundle(b: ResourceBundle): boolean {
  return Object.keys(addBundles(b)).length === 0;
}

/**
 * Credit a bundle to the (already cloned) profile. Blessed Charges above BLESSED_CAP become
 * BLESSED_OVERFLOW_VENOM Venom each. Returns the Venom made from overflow.
 */
export function credit(p: Profile, b: ResourceBundle): number {
  const w = p.wallet;
  w.fangsteel += nn(b.fangsteel);
  for (const g of SCALE_GRADES) w.scales[g] += nn(b.scales?.[g]);
  w.venom += nn(b.venom);
  w.moltskin += nn(b.moltskin);
  w.eyeFragments += nn(b.eyeFragments);
  w.sigils += nn(b.sigils);
  p.currencies.gold += nn(b.gold);
  const blessed = w.blessed + nn(b.blessed);
  const over = Math.max(0, blessed - BLESSED_CAP);
  w.blessed = Math.min(BLESSED_CAP, blessed);
  w.venom += over * BLESSED_OVERFLOW_VENOM;
  return over * BLESSED_OVERFLOW_VENOM;
}

/** What is missing to pay `b` (empty bundle = affordable). Scales must match grade exactly here. */
export function shortfall(p: Pick<Profile, 'wallet' | 'currencies'>, b: ResourceBundle): ResourceBundle {
  const w = p.wallet;
  const out: ResourceBundle = {};
  const diff = (have: number, need: number | undefined): number => Math.max(0, nn(need) - have);
  if (diff(w.fangsteel, b.fangsteel)) out.fangsteel = diff(w.fangsteel, b.fangsteel);
  if (diff(w.venom, b.venom)) out.venom = diff(w.venom, b.venom);
  if (diff(w.moltskin, b.moltskin)) out.moltskin = diff(w.moltskin, b.moltskin);
  if (diff(w.eyeFragments, b.eyeFragments)) out.eyeFragments = diff(w.eyeFragments, b.eyeFragments);
  if (diff(w.sigils, b.sigils)) out.sigils = diff(w.sigils, b.sigils);
  if (diff(w.blessed, b.blessed)) out.blessed = diff(w.blessed, b.blessed);
  if (diff(p.currencies.gold, b.gold)) out.gold = diff(p.currencies.gold, b.gold);
  for (const g of SCALE_GRADES) if (diff(w.scales[g], b.scales?.[g])) out.scales = { ...out.scales, [g]: diff(w.scales[g], b.scales?.[g]) };
  return out;
}

export function canAfford(p: Pick<Profile, 'wallet' | 'currencies'>, b: ResourceBundle): boolean {
  return isEmptyBundle(shortfall(p, b));
}

/** Debit a bundle (caller checked canAfford). */
export function debit(p: Profile, b: ResourceBundle): void {
  const w = p.wallet;
  w.fangsteel -= nn(b.fangsteel);
  for (const g of SCALE_GRADES) w.scales[g] -= nn(b.scales?.[g]);
  w.venom -= nn(b.venom);
  w.moltskin -= nn(b.moltskin);
  w.eyeFragments -= nn(b.eyeFragments);
  w.sigils -= nn(b.sigils);
  w.blessed -= nn(b.blessed);
  p.currencies.gold -= nn(b.gold);
}

/**
 * Split `count` Scales owed at band `grade` over the scales on hand: the band grade first, then
 * higher grades 1:1 (trade-down, crafting.md §1.1). null when not enough in total.
 */
export function planScales(have: Record<ScaleGrade, number>, grade: ScaleGrade, count: number): Partial<Record<ScaleGrade, number>> | null {
  const out: Partial<Record<ScaleGrade, number>> = {};
  let left = count;
  for (const g of SCALE_GRADES.slice(gradeIndex(grade))) {
    if (left <= 0) break;
    const take = Math.min(left, have[g]);
    if (take > 0) out[g] = take;
    left -= take;
  }
  return left > 0 ? null : out;
}

export function scaleCount(b: ResourceBundle): number {
  let n = 0;
  for (const g of SCALE_GRADES) n += b.scales?.[g] ?? 0;
  return n;
}
